/** @jsxImportSource @emotion/react */
import { cx } from '@emotion/css';
import type { FC } from 'react';
import { firstReadable } from '../../utils';

type Props = React.ComponentProps<'svg'> & {
  className?: string;
  /** Is this logo used on a dark background, use the light logo */
  light?: boolean;
  /** One color logo */
  mono?: boolean;
  /** Icon only, no text */
  icon?: boolean;
  /** Text only, no icon */
  wordmark?: boolean;
  /** Beta logo color */
  beta?: boolean;
  /** Display logo with a background */
  background?: boolean;
  /** Pass in a custom background color */
  backgroundColor?: string;
  /** Pass in a custom logo color */
  logoColor?: string;
};

const ODSLogo: FC<Props> = ({
  className,
  light,
  mono,
  icon,
  wordmark,
  beta,
  backgroundColor,
  logoColor,
  background,
  ...componentProps
}) => {
  const grey04 = '#191919';
  const coreBlue = '#019EFF';
  const coreWhite = '#FFFFFF';
  let textColor = grey04;
  let bomberColor = coreBlue;
  let bgColor = coreWhite;

  if (!backgroundColor) {
    if (logoColor) {
      textColor = logoColor;
      bomberColor = logoColor;
    } else if (light) {
      textColor = coreWhite;
      bgColor = grey04;
    }

    if (mono === true) {
      bomberColor = textColor;
    }
  } else {
    const color =
      logoColor ?? firstReadable(backgroundColor, [coreWhite, coreBlue, grey04], 3).toString();
    bgColor = backgroundColor;
    textColor = color;
    bomberColor = color;
  }

  if (beta) {
    bomberColor = '#5B687B';
  }

  if (icon) {
    let width = 32;
    let height = 32;
    const padding = height / 2;
    let x = 0;
    let y = 0;
    if (background) {
      x = padding * -1;
      y = x;
      width += padding * 2;
      height += padding * 2;
    }
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`${x} ${y} ${width} ${height}`}
        className={cx(className)}
        css={{ display: 'block' }}
        {...componentProps}
      >
        {background && <rect fill={bgColor} x={x} y={y} width={width} height={height} />}
        <path
          fill={bomberColor}
          d="M0 .259v9.739c0 .143.115.259.257.259h10.156c.142 0 .257.116.257.259v7.507c0 .23.277.346.438.183L28.712.442A.26.26 0 0 0 28.53 0H.257A.258.258 0 0 0 0 .259Zm31.741 31.744h-9.739a.258.258 0 0 1-.259-.257V21.59a.258.258 0 0 0-.259-.257h-7.507a.257.257 0 0 1-.183-.439L31.558 3.291a.26.26 0 0 1 .442.181v28.274a.258.258 0 0 1-.259.257Z"
        />
      </svg>
    );
  }

  if (wordmark) {
    let width = 278;
    let height = 54;
    const padding = height / 2;
    let x = 0;
    let y = 0;
    if (background) {
      x = padding * -1;
      y = x;
      width += padding * 2;
      height += padding * 2;
    }
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`${x} ${y} ${width} ${height}`}
        className={cx(className)}
        css={{ display: 'block' }}
        {...componentProps}
      >
        {background && <rect fill={bgColor} x={x} y={y} width={width} height={height} />}
        <path
          fill={textColor}
          d="M21.29 53.5c-6.261 0-11.236-1.747-14.925-5.242C2.677 44.715.832 39.62.832 32.97V21.03c0-6.649 1.845-11.72 5.533-15.215C10.054 2.272 15.03.5 21.29.5c6.26 0 11.236 1.772 14.924 5.315 3.737 3.494 5.606 8.566 5.606 15.215v11.94c0 6.649-1.869 11.745-5.606 15.288-3.688 3.495-8.663 5.242-14.924 5.242Zm0-7.062c4.028 0 7.134-1.165 9.318-3.494 2.184-2.379 3.276-5.606 3.276-9.683V20.739c0-4.077-1.092-7.28-3.276-9.61-2.184-2.378-5.29-3.567-9.318-3.567-3.932 0-7.014 1.189-9.246 3.567-2.233 2.33-3.35 5.533-3.35 9.61v12.522c0 4.077 1.117 7.304 3.35 9.683 2.232 2.33 5.314 3.494 9.246 3.494Zm27.144 6.042V16.59h7.353v5.387h1.165c.68-1.456 1.893-2.815 3.64-4.077s4.344-1.893 7.79-1.893c2.718 0 5.12.607 7.207 1.82 2.136 1.213 3.81 2.936 5.024 5.169 1.213 2.184 1.82 4.83 1.82 7.935v21.55h-7.499V31.514c0-3.107-.776-5.388-2.33-6.844-1.553-1.504-3.688-2.257-6.406-2.257-3.106 0-5.606 1.02-7.499 3.058-1.844 2.039-2.766 5-2.766 8.882v18.128h-7.499Zm58.332 1.02c-3.64 0-6.82-.752-9.537-2.257-2.718-1.553-4.854-3.713-6.407-6.48-1.505-2.814-2.257-6.066-2.257-9.755v-.873c0-3.738.752-6.99 2.257-9.756 1.505-2.815 3.592-4.975 6.261-6.48 2.718-1.552 5.848-2.329 9.391-2.329 3.446 0 6.456.777 9.028 2.33 2.621 1.504 4.659 3.616 6.115 6.334 1.456 2.717 2.184 5.896 2.184 9.537v2.839H96.209c.097 3.155 1.14 5.678 3.13 7.571 2.039 1.845 4.563 2.767 7.572 2.767 2.815 0 4.927-.631 6.334-1.893 1.456-1.262 2.572-2.718 3.349-4.368l6.188 3.203c-.679 1.36-1.674 2.79-2.985 4.295-1.262 1.505-2.936 2.767-5.023 3.786-2.087 1.02-4.756 1.529-8.008 1.529ZM96.282 30.858h19.875c-.194-2.717-1.165-4.829-2.912-6.333-1.747-1.553-4.028-2.33-6.843-2.33-2.815 0-5.121.777-6.916 2.33-1.748 1.504-2.816 3.616-3.204 6.334ZM150.664 53.5c-3.494 0-6.188-.631-8.081-1.893-1.844-1.262-3.227-2.67-4.149-4.222h-1.165v5.096h-7.353V1.519h7.498v19.948h1.165c.583-.97 1.359-1.893 2.33-2.766.97-.923 2.257-1.675 3.858-2.257 1.602-.583 3.568-.874 5.897-.874 3.009 0 5.776.728 8.3 2.184 2.524 1.456 4.538 3.567 6.042 6.334 1.505 2.766 2.257 6.067 2.257 9.901v1.092c0 3.883-.776 7.207-2.329 9.974-1.505 2.718-3.519 4.805-6.043 6.26-2.475 1.457-5.218 2.185-8.227 2.185Zm-2.184-6.552c3.252 0 5.921-1.044 8.008-3.13 2.136-2.088 3.204-5.073 3.204-8.955v-.656c0-3.834-1.044-6.794-3.131-8.881-2.087-2.087-4.78-3.13-8.081-3.13-3.203 0-5.872 1.043-8.008 3.13-2.087 2.087-3.131 5.047-3.131 8.881v.656c0 3.882 1.044 6.867 3.131 8.954 2.136 2.087 4.805 3.13 8.008 3.13Zm25.162 5.532V16.59h7.353v4.222h1.165c.583-1.505 1.505-2.597 2.767-3.276 1.31-.728 2.912-1.092 4.805-1.092h4.295v6.77h-4.586c-2.427 0-4.417.68-5.97 2.039-1.553 1.31-2.33 3.349-2.33 6.115v21.113h-7.499Zm57.411 1.02c-3.64 0-6.819-.752-9.537-2.257-2.718-1.553-4.854-3.713-6.407-6.48-1.505-2.814-2.257-6.066-2.257-9.755v-.873c0-3.738.752-6.99 2.257-9.756 1.505-2.815 3.592-4.975 6.261-6.48 2.718-1.552 5.848-2.329 9.391-2.329 3.446 0 6.456.777 9.028 2.33 2.621 1.504 4.659 3.616 6.115 6.334 1.456 2.717 2.184 5.896 2.184 9.537v2.839h-27.592c.097 3.155 1.141 5.678 3.131 7.571 2.038 1.845 4.562 2.767 7.571 2.767 2.815 0 4.927-.631 6.334-1.893 1.456-1.262 2.572-2.718 3.349-4.368l6.188 3.203c-.679 1.36-1.674 2.79-2.985 4.295-1.262 1.505-2.936 2.767-5.023 3.786-2.087 1.02-4.757 1.529-8.008 1.529Zm-10.484-22.642h19.875c-.194-2.717-1.165-4.829-2.912-6.333-1.747-1.553-4.028-2.33-6.843-2.33-2.815 0-5.121.777-6.917 2.33-1.747 1.504-2.815 3.616-3.203 6.334Zm38.762 21.622V22.924h-10.046V16.59h10.046V8.727c0-2.184.655-3.932 1.966-5.242 1.31-1.31 3.058-1.966 5.242-1.966h9.246v6.334h-6.844c-1.407 0-2.111.728-2.111 2.184v6.552h10.338v6.334H266.83v29.558h-7.499Zm-59.28 0V16.59h7.706v35.89h-7.706Zm0-42.025V1.519h7.706v8.936h-7.706Z"
        />
      </svg>
    );
  }

  let width = 320;
  let height = 54;
  const padding = height / 2;
  let x = 0;
  let y = 0;
  if (background) {
    x = padding * -1;
    y = x;
    width += padding * 2;
    height += padding * 2;
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`${x} ${y} ${width} ${height}`}
      className={cx(className)}
      css={{ display: 'block' }}
      {...componentProps}
    >
      {background && <rect fill={bgColor} x={x} y={y} width={width} height={height} />}
      <path
        fill={bomberColor}
        d="M1.52 1.757v8.929c0 .13.105.237.236.237h9.311c.13 0 .236.107.236.238v6.882c0 .212.254.318.402.168l16.14-16.286a.238.238 0 0 0-.166-.405H1.755a.236.236 0 0 0-.235.237ZM30.622 30.86h-8.929a.236.236 0 0 1-.237-.235v-9.312a.237.237 0 0 0-.238-.235h-6.883a.235.235 0 0 1-.168-.402L30.456 4.537a.238.238 0 0 1 .405.167v25.92c0 .13-.106.236-.238.236Z"
      />
      <path
        fill={textColor}
        d="M63.32 53.5c-6.261 0-11.236-1.747-14.925-5.242-3.688-3.543-5.533-8.639-5.533-15.288V21.03c0-6.649 1.845-11.72 5.533-15.215C52.084 2.272 57.06.5 63.32.5c6.26 0 11.235 1.772 14.924 5.315 3.737 3.494 5.606 8.566 5.606 15.215v11.94c0 6.649-1.869 11.745-5.606 15.288-3.689 3.495-8.663 5.242-14.924 5.242Zm0-7.062c4.028 0 7.134-1.165 9.318-3.494 2.184-2.379 3.276-5.606 3.276-9.683V20.739c0-4.077-1.092-7.28-3.276-9.61-2.184-2.378-5.29-3.567-9.318-3.567-3.932 0-7.014 1.189-9.246 3.567-2.233 2.33-3.35 5.533-3.35 9.61v12.522c0 4.077 1.117 7.304 3.35 9.683 2.232 2.33 5.314 3.494 9.246 3.494Zm27.144 6.042V16.59h7.353v5.387h1.165c.68-1.456 1.893-2.815 3.64-4.077s4.344-1.893 7.79-1.893c2.718 0 5.12.607 7.207 1.82 2.136 1.213 3.81 2.936 5.024 5.169 1.213 2.184 1.82 4.83 1.82 7.935v21.55h-7.499V31.514c0-3.107-.776-5.388-2.33-6.844-1.553-1.504-3.688-2.257-6.406-2.257-3.106 0-5.606 1.02-7.499 3.058-1.844 2.039-2.766 5-2.766 8.882v18.128h-7.499Zm58.332 1.02c-3.64 0-6.819-.752-9.537-2.257-2.718-1.553-4.854-3.713-6.407-6.48-1.505-2.814-2.257-6.066-2.257-9.755v-.873c0-3.738.752-6.99 2.257-9.756 1.505-2.815 3.592-4.975 6.261-6.48 2.718-1.552 5.848-2.329 9.391-2.329 3.446 0 6.456.777 9.028 2.33 2.621 1.504 4.659 3.616 6.115 6.334 1.456 2.717 2.184 5.896 2.184 9.537v2.839h-27.592c.097 3.155 1.141 5.678 3.131 7.571 2.038 1.845 4.562 2.767 7.571 2.767 2.815 0 4.927-.631 6.334-1.893 1.456-1.262 2.572-2.718 3.349-4.368l6.188 3.203c-.679 1.36-1.674 2.79-2.985 4.295-1.262 1.505-2.936 2.767-5.023 3.786-2.087 1.02-4.756 1.529-8.008 1.529Zm-10.484-22.642h19.875c-.194-2.717-1.165-4.829-2.912-6.333-1.747-1.553-4.028-2.33-6.843-2.33-2.815 0-5.121.777-6.917 2.33-1.747 1.504-2.815 3.616-3.203 6.334ZM192.694 53.5c-3.494 0-6.188-.631-8.081-1.893-1.844-1.262-3.227-2.67-4.149-4.222h-1.165v5.096h-7.353V1.519h7.498v19.948h1.165c.583-.97 1.359-1.893 2.33-2.766.97-.923 2.257-1.675 3.858-2.257 1.602-.583 3.568-.874 5.897-.874 3.009 0 5.776.728 8.3 2.184 2.524 1.456 4.538 3.567 6.042 6.334 1.505 2.766 2.257 6.067 2.257 9.901v1.092c0 3.883-.776 7.207-2.329 9.974-1.505 2.718-3.519 4.805-6.043 6.26-2.475 1.457-5.218 2.185-8.227 2.185Zm-2.184-6.552c3.252 0 5.921-1.044 8.008-3.13 2.136-2.088 3.204-5.073 3.204-8.955v-.656c0-3.834-1.044-6.794-3.131-8.881-2.087-2.087-4.78-3.13-8.081-3.13-3.203 0-5.873 1.043-8.008 3.13-2.087 2.087-3.131 5.047-3.131 8.881v.656c0 3.882 1.044 6.867 3.131 8.954 2.135 2.087 4.805 3.13 8.008 3.13Zm25.162 5.532V16.59h7.353v4.222h1.165c.583-1.505 1.505-2.597 2.767-3.276 1.31-.728 2.912-1.092 4.805-1.092h4.295v6.77h-4.586c-2.427 0-4.417.68-5.97 2.039-1.553 1.31-2.33 3.349-2.33 6.115v21.113h-7.499Zm57.411 1.02c-3.64 0-6.819-.752-9.537-2.257-2.718-1.553-4.854-3.713-6.407-6.48-1.505-2.814-2.257-6.066-2.257-9.755v-.873c0-3.738.752-6.99 2.257-9.756 1.505-2.815 3.592-4.975 6.261-6.48 2.718-1.552 5.848-2.329 9.391-2.329 3.446 0 6.456.777 9.028 2.33 2.621 1.504 4.659 3.616 6.115 6.334 1.456 2.717 2.184 5.896 2.184 9.537v2.839h-27.592c.097 3.155 1.141 5.678 3.131 7.571 2.038 1.845 4.562 2.767 7.571 2.767 2.815 0 4.927-.631 6.334-1.893 1.456-1.262 2.572-2.718 3.349-4.368l6.188 3.203c-.679 1.36-1.674 2.79-2.985 4.295-1.262 1.505-2.936 2.767-5.023 3.786-2.087 1.02-4.757 1.529-8.008 1.529Zm-10.484-22.642h19.875c-.194-2.717-1.165-4.829-2.912-6.333-1.747-1.553-4.028-2.33-6.843-2.33-2.815 0-5.121.777-6.917 2.33-1.747 1.504-2.815 3.616-3.203 6.334Zm38.762 21.622V22.924h-10.047V16.59h10.047V8.727c0-2.184.655-3.932 1.966-5.242 1.31-1.31 3.058-1.966 5.242-1.966h9.246v6.334h-6.844c-1.407 0-2.111.728-2.111 2.184v6.552h10.338v6.334H308.86v29.558h-7.499Zm-59.28 0V16.59h7.706v35.89h-7.706Zm0-42.025V1.519h7.706v8.936h-7.706Z"
      />
    </svg>
  );
};

export default ODSLogo;
